import { Link } from "react-router-dom";
import { ArrowRight, Leaf } from "lucide-react";
import { IMG, BUSH_BOTANICS_PRODUCTS } from "../data/content";
import Seo from "../components/Seo";

export default function BushBotanics() {
  return (
    <div data-testid="page-bush-botanics">
      <Seo
        title="Bush Botanics — Native plant enterprises beyond restoration"
        description="Bush Botanics supports communities to develop enterprises based on culturally significant species — traditional medicines, bush foods, essential oils, natural skincare and cultural tourism."
        path="/bush-botanics"
      />

      {/* Hero */}
      <section className="border-b border-rule">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="stamp">Bush Botanics</span>
              <span className="specimen-label">Native plants — beyond restoration</span>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.98]">
              Native plants provide opportunities that <em className="text-eucalypt">extend beyond restoration.</em>
            </h1>
            <p className="mt-10 text-base md:text-lg leading-relaxed text-ink/85 max-w-2xl">
              Native plants have always provided food, medicine, materials and cultural resources
              for Indigenous communities. Bush Botanics explores how these relationships can
              continue to create opportunities today.
            </p>
            <p className="mt-4 text-base md:text-lg leading-relaxed text-ink/85 max-w-2xl">
              Working with culturally significant species, communities can develop products and
              enterprises based on traditional medicines, bush foods, natural skincare products,
              essential oils, handmade paper, natural fibres and cultural tourism experiences.{" "}
              <a
                href="https://www.bushbotanics.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="bush-botanics-external-link"
                className="link-mono text-eucalypt inline whitespace-nowrap"
              >
                www.bushbotanics.com
              </a>
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="polaroid">
              <img
                src={IMG.fruits}
                alt="Native plant fruits — culturally significant species"
                className="w-full h-[460px] object-cover"
              />
            </div>
            <p className="mt-3 text-xs text-muted italic">
              Bush food species — knowledge, value and Country in one.
            </p>
          </div>
        </div>
      </section>

      {/* Founding idea */}
      <section className="border-b border-rule bg-bark text-chalk">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="rounded-2xl overflow-hidden aspect-[4/5] border border-chalk/15">
              <img
                src={IMG.mission}
                alt="Linda Eddy and William Martin on Country with native shrubs"
                className="w-full h-full object-cover"
                loading="lazy"
                data-testid="bush-botanics-linda-photo"
              />
            </div>
            <p className="mt-4 font-mono text-[10px] tracking-[0.22em] uppercase text-chalk/60 italic">
              Linda Eddy &amp; William Martin, on Country
              <span className="text-chalk/40 not-italic normal-case tracking-normal ml-2">
                · photo by Mai Lin, used with permission from Elder
              </span>
            </p>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <p className="specimen-label text-chalk/60 mb-6">Founding Idea</p>
            <blockquote className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.15]">
              Communities should be able to benefit from the <em className="text-ochre">knowledge, species and landscapes</em> they are caring for.
            </blockquote>
            <p className="mt-8 specimen-label text-ochre">— Bush Botanics, foundational principle</p>
          </div>
        </div>
      </section>

      {/* Product / enterprise areas */}
      <section className="border-b border-rule">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <p className="section-num mb-3">Enterprise Areas</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.02] mb-14 max-w-4xl">
            Products and enterprises grounded in cultural knowledge and care for Country.
          </h2>
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-rule border border-rule"
            data-testid="bush-botanics-products"
          >
            {BUSH_BOTANICS_PRODUCTS.map((p, i) => (
              <div
                key={p.title}
                className="bg-card p-7 md:p-8 flex flex-col gap-4 min-h-[220px]"
                data-testid={`bush-botanics-product-${i}`}
              >
                <div className="flex items-center justify-between">
                  <span className="specimen-label">No. {String(i + 1).padStart(2, "0")}</span>
                  <Leaf size={18} className="text-eucalypt" />
                </div>
                <h3 className="font-serif text-2xl md:text-3xl leading-tight">{p.title}</h3>
                <p className="text-sm md:text-base text-ink/80 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why it matters */}
      <section className="border-b border-rule">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <img
              src={IMG.essentialOils}
              alt="Essential oil distillation — value-add from native species"
              className="w-full h-[460px] object-cover border border-rule p-2 bg-cream"
            />
            <p className="mt-3 text-xs text-muted italic">
              Steam distillation — turning culturally significant species into essential oils, on-Country.
            </p>
          </div>
          <div className="lg:col-span-7">
            <p className="section-num mb-3">Why It Matters</p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight max-w-3xl">
              Pathways for employment and enterprise — without separating from culture or Country.
            </h2>
            <div className="mt-10 space-y-5 text-base md:text-lg text-ink/85 leading-relaxed max-w-2xl">
              <p>
                These opportunities create pathways for employment and enterprise development while
                strengthening cultural knowledge and connections to Country.
              </p>
              <p>
                When the species, the knowledge and the production all stay with community, the
                enterprise stays connected to its source — and the benefits flow back to the
                landscapes and people that sustained the knowledge in the first place.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section>
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-24 text-center">
          <p className="section-num mb-8">Talk to us</p>
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.15]">
            If your community is exploring native plant enterprise, <em className="text-eucalypt">we&rsquo;d like to hear from you.</em>
          </h2>
          <div className="mt-12">
            <Link
              to="/contact"
              data-testid="bush-botanics-cta"
              className="inline-flex items-center gap-3 bg-eucalypt text-chalk px-8 py-4 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-eucalypt-dark rounded-full hover:-translate-y-0.5 transition-all"
            >
              Start a conversation <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
